import React, {useEffect, useState} from "react";
import { useLocation, useNavigate  } from 'react-router-dom';
import { AppDispatch, RootState } from "../../store/types/store";
import { useDispatch, useSelector } from "react-redux";
import { submitPaymentRequestTokenValidationRequest } from "../../store/slices/payment-slice";
import {
  APP_API_URL,
  ENV, PAYMENT_GATEWAY_MERCHANT_ID,
  PAYMENT_GATEWAY_SDK,
  REACT_APP_BASE_URL
} from "../../constants";

import Header from "../../components/payments/Header";
import Footer from "../../components/payments/Footer";
import {Loader} from "../../components/Loader";
import {Spin} from "antd";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faCheck, faXmark} from "@fortawesome/free-solid-svg-icons";

declare global {
  interface Window {
    payhere: any;
  }
}

const PaymentsSummary = () => {
  const dispatch: AppDispatch = useDispatch();
  const location = useLocation();

  const [checkboxes, setCheckboxes] = useState({
    checkbox_tos_consent: false,
    checkbox_charge_consent: false
  });

  // Function to handle checkbox change
  const handleCheckboxChange = (event: any) => {
    const { name, checked } = event.target;

    console.log(`Checkbox ${name} changed to:`, checked); // Debugging log

    setCheckboxes((prev) => ({
      ...prev,
      [name]: checked,
    }));
  };

  // Enable button only if checkbox1 and checkbox2 are checked
  const isButtonDisabled = !(checkboxes.checkbox_tos_consent);

  let paymentRef: String | null = null;
  let navigate = useNavigate();

  const { response, loading } = useSelector(
    (state: RootState) => state.payment
  );

  useEffect(() => {
    // use response here
    console.log(response);
  }, [JSON.stringify(response)]);

  useEffect(() => {
    const script = document.createElement("script");
    script.src = PAYMENT_GATEWAY_SDK;
    script.type = "text/javascript";
    script.async = true;
    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script); // Cleanup on component unmount
    };
  }, []);

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    paymentRef = queryParams.get('ref');

    let payload = {
      paymentToken: paymentRef
    };
    dispatch(submitPaymentRequestTokenValidationRequest(payload));
  }, []);

  // Payment event handlers
  useEffect(() => {
    const existingScript = document.querySelector('script[src="'+PAYMENT_GATEWAY_SDK+'"]');

    if (!existingScript && response) {
      console.log("Adding PayHere SDK script...");
      const script = document.createElement('script');
      script.src = PAYMENT_GATEWAY_SDK;
      script.type = "text/javascript";
      script.async = true;

      script.onload = () => {
        console.log("✅ PayHere SDK script loaded, waiting for availability...");

        // Wait for window.payhere to be available
        let attempts = 0;
        const checkSDK = setInterval(() => {
          if (window.payhere) {
            console.log("✅ PayHere SDK is now available!");
            clearInterval(checkSDK);
            attachPayHereEventHandlers(); // Attach event handlers after SDK is ready
          } else {
            attempts++;
            if (attempts > 10) { // Stop checking after 10 attempts (~2 seconds)
              console.error("❌ PayHere SDK is still not available.");
              clearInterval(checkSDK);
            }
          }
        }, 200);
      };

      script.onerror = () => {
        console.error("❌ Failed to load PayHere SDK.");
      };

      document.body.appendChild(script);
    } else {
      console.log("ℹ️ PayHere SDK already loaded, waiting for availability...");

      let attempts = 0;
      const checkSDK = setInterval(() => {
        if (window.payhere) {
          console.log("✅ PayHere SDK is now available!");
          clearInterval(checkSDK);
          if(response) {
            attachPayHereEventHandlers(); // Attach event handlers after SDK is ready
          }
        } else {
          attempts++;
          if (attempts > 10) {
            console.error("❌ PayHere SDK is still not available.");
            clearInterval(checkSDK);
          }
        }
      }, 200);
    }
  }, [JSON.stringify(response)]);

  // Attach PayHere event handlers
  const attachPayHereEventHandlers = () => {
    if (window.payhere) {
      console.log("✅ Attaching PayHere event handlers...");
      window.payhere.onCompleted = (orderId: string) => {
        console.log("✅ Payment completed. OrderID:", orderId);
        console.log('response')
        console.log(paymentRef)
        navigate("/payments/done?status=success&orderId=" + orderId +
            "&paymentRef=" + response?.body.paymentRequest.uuid +
            "&returnURL=" + response?.body.paymentRequest.appReturnURL +
            "&appName=" + response?.body.paymentRequest.appName
        );
      };

      window.payhere.onDismissed = () => {
        console.log("⚠️ Payment dismissed.");
        navigate("/payments/done?status=cancelled")
      };

      window.payhere.onError = (error: string) => {
        console.log("❌ Payment Error:", error);
        navigate("/payments/done?status=failed")
      };
    } else {
      console.error("❌ PayHere SDK is still not available. Event handlers not attached.");
    }
  };

  // Function to start the payment process
  const handlePayment = () => {
    if(response){
      const payment = {
        sandbox: ENV == "development",
        merchant_id: PAYMENT_GATEWAY_MERCHANT_ID, // Replace with your Merchant ID
        return_url: `${REACT_APP_BASE_URL}/done`, // Important
        cancel_url: `${REACT_APP_BASE_URL}/done`, // Important
        notify_url: `${APP_API_URL}/payment/api/v1/payments/receive-payment-status`,
        order_id: response.body.meta.orderId,
        items: response.body.paymentRequest.productName,
        amount: response.body.meta.amount,
        currency: response.body.meta.currency,
        hash: response.body.meta.ipgHash,
        first_name: response.body.paymentRequest.customerName.split(" ")[0] || "Unknown",
        last_name: response.body.paymentRequest.customerName.split(" ")[1] || "Unknown",
        email:  response.body.paymentRequest.customerEmail,
        phone: "",
        address: "",
        city: "",
        country: "",
        delivery_address: "",
        delivery_city: "",
        delivery_country: "",
        custom_1: response.body.paymentRequest.uuid,
        custom_2: "",
      };

      console.log(payment);

      // Trigger PayHere Payment
      if (window.payhere) {
        window.payhere.startPayment(payment);
      } else {
        alert("Payment system is not loaded. Please refresh the page.");
      }
    }
  };

  return (
    <Spin spinning={loading || !response} indicator={<Loader />}>
    <div className="container">
      <div className="container mt-5">
        <div className="row justify-content-center mb-3">
          <div className="col-md-10">
            <Header />
          </div>
        </div>

        {response && !loading ? (

         <div className="row justify-content-center">
          <div className="col-md-5">
            <p>
              Subscribe to <strong>{response && response.body.paymentRequest.productName}</strong> Plan
            </p>
            <p className="my-4 package">
              <strong>${response && response.body.paymentRequest.paymentTotalAmount}</strong>{" "}
              <span className="text-muted">{response && response.body.paymentRequest.billingCycleType == "monthly" ? "per month " : "per lifetime"}</span>
            </p>
            <div className="border p-3 rounded">
              <div style={{ display: "flex", alignItems: "center" }}>
                <div className="product-logo"></div>
                <p className="mb-1 text-heading">
                  <strong>{response && response.body.paymentRequest.productName}</strong>
                </p>
              </div>
              <p className="mb-1 text-muted text-thin">{response && response.body.paymentRequest.billingCycleType == "monthly" ? "Billed monthly " : "Onetime payment "}</p>
              <div className="d-flex justify-content-between">
                <span>Subtotal</span>
                <span>${response && response.body.paymentRequest.productPlanPrice}</span>
              </div>
              <div>
                {response && response.body.paymentRequest.additionalCharges.map(charge => (
                    <div key={charge._id} className="d-flex justify-content-between">
                      <span>{charge.chargeName}</span>
                      <span>${Number(charge.chargeAmount).toFixed(2)}</span>
                    </div>
                ))}
              </div>
              <hr />
              <div className="d-flex justify-content-between">
                <span>
                  <strong>Total</strong>
                </span>
                <strong>${response && response.body.paymentRequest.paymentTotalAmount}</strong>
              </div>
            </div>
          </div>

          <div className="col-md-5">
            <form
                className="pre-payment-form"
                onSubmit={(e) => {
                  e.preventDefault();
                }}
            >
              <p className="text-heading">
                <strong>Contact Information</strong>
              </p>
              <div className="mb-3">
                <input
                    type="email"
                    className="form-control read-only-field contact-info-field-readonly"
                    id="email"
                    readOnly
                    placeholder={response && response.body.paymentRequest.customerName || ""}
                />
              </div>

              <div className="mb-3">
                <input
                    type="email"
                    className="form-control read-only-field contact-info-field-readonly"
                    id="email"
                    readOnly
                    placeholder={response && response.body.paymentRequest.customerEmail || ""}
                />
              </div>

              <div className="form-check mb-3">
                <input
                    type="checkbox"
                    className="form-check-input"
                    name="checkbox_tos_consent"
                    id="save-info"
                    checked={checkboxes.checkbox_tos_consent}
                    onChange={handleCheckboxChange}
                />
                <label className="form-check-label" htmlFor="save-info">
                  I have read and understood Symmatics's&nbsp;
                  <a
                      className="link-muted"
                      href="https://symmatics.com/terms-of-service"
                  >
                    Terms
                  </a>
                  ,&nbsp;
                  <a
                      className="link-muted"
                      href="https://symmatics.com/privacy-policy"
                  >
                    Privacy Policy
                  </a>
                  , and&nbsp;
                  <a
                      className="link-muted"
                      href="https://symmatics.com/products/winston/refund-policy"
                  >
                    Refund Policy
                  </a>
                  .
                </label>
              </div>

              <div className="form-check mb-4">
                <p className="text-muted">
                  <i className="bi bi-lock-fill"></i>This payment form is
                  protected by industry-standard SSL encryption, ensuring your
                  personal and payment details remain safe.
                </p>
              </div>

              <button
                  type="button"
                  id="payhere-payment"
                  className="btn btn-success w-100"
                  onClick={handlePayment}
                  disabled={isButtonDisabled}
              >
                Continue
              </button>
            </form>
          </div>
        </div>

        ) : (


            <div className="row justify-content-center">
              <div className="col-md-8">
                <p className="text-heading">
                  <strong>
                    <FontAwesomeIcon
                        size="2x"
                        color={"red"}
                        icon={faXmark}
                        style={{ verticalAlign: "middle", marginBottom: "5px", marginRight: "10px" }}
                    />
                    Invalid Payment Request
                  </strong>
                </p>

                <p>Hmmm, looks like you've followed an invalid payment request. Kindly contact us for any questions or concerns.</p>

              </div>
              <div className="col-md-2"></div>
            </div>

          )

        }
        <div className="row justify-content-center mt-5">
          <div className="col-md-10">
            <Footer />
          </div>
        </div>
      </div>
    </div>
    </Spin>
  );
};

export default PaymentsSummary;
